import {useEffect, useRef} from 'react';
import {useSelector} from "react-redux";
import {
  selectCurrentPlayer,
  selectFinalRound,
  selectGameResults,
  selectGameType,
  selectPhase,
  selectRound,
  selectWarPhaseStage
} from "../../redux/reducers/sharedStateReducer/selectors";
import {usePrev} from "../../hooks/react/usePrev";
import {PHASES, ROUND_PHASES, WAR_PHASE_STAGES} from "../../constants/phases";
import {checkIfPreFinalRound, checkIfPreFinalRoundOf} from "../../utils/checkIfFinalRound";
import {isPlayerFaction} from "../../utils/factions/isPlayerFaction";
import {FACTIONS} from "../../constants/factions";
import {clearInspectedHex, showToast} from "../../redux/actions";
import {REGISTRY_SINGLETON} from "../../scenes/Registry";
import {useIsCurrentUserTurn} from "../../utils/gameState/user/useIsCurrentUserCurrentPlayer";
import {GAME_TYPES} from "../../constants/game";
import store from "../../redux/store";
import {checkIfCurrentUserTurnInState} from "../../utils/gameState/user/checkIfCurrentUserTurnInState";
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer
} from "../../utils/gameState/supply/parallelSupplySelectors";
import {showTurnOverlay} from "../TurnOverlay/showTurnOverlay";
import {resolveFactionSkinIconPath} from "../../utils/factions/resolveFactionSkinIconPath";
import {checkIfTurnWillBeAutoCompleted} from "../../utils/gameState/turn/executePhaseAutoActions";
import {trackAdGoal, AD_GOALS} from "../../analytics/adTracking";
import {selectIsGameStarted} from "../../redux/reducers/sharedStateReducer/selectors";
import {
  selectInspectedHexKey,
  selectIsCityCaptureAnimationPlaying,
  selectIsRuinsIncomeAnimationAwaited,
} from "../../redux/reducers/privateStateReducer/selectors";

// Задержка предупреждения о последнем раунде найма: за это время приходит
// автопропуск этапа и успевает отыграть оверлей начала хода
const PRE_FINAL_ROUND_RECRUITMENT_TOAST_DELAY_MS = 1000;

/**
 * Отложенный тост «свой ход»: показывается через задержку и только если момент
 * к этому времени не прошел — этап/фаза не пропущены автоматикой и ход все еще
 * у того же игрока. Состояние читается заново из стора, а не берется из
 * замыкания: за секунду оно успевает измениться.
 *
 * @param checkIfMomentStillActual - (state) => boolean, специфичная часть
 * проверки: фаза, этап, фракция
 * @param message
 * @returns функция очистки таймера
 */
const schedulePreFinalRoundToast = (checkIfMomentStillActual, message) => {
  const timeoutId = setTimeout(() => {
    const currentState = store.getState();
    // В одновременной фазе снабжения «свой ход» — пока свой действующий игрок
    // не закончил свою часть фазы (см. checkIfCurrentUserTurnInState)
    const isOwnTurn = checkIfCurrentUserTurnInState(currentState);

    if (!isOwnTurn
      || !checkIfPreFinalRound(currentState)
      || !checkIfMomentStillActual(currentState)
    ) {
      return;
    }

    store.dispatch(showToast(message, 'info', 7000));
  }, PRE_FINAL_ROUND_RECRUITMENT_TOAST_DELAY_MS);

  return () => clearTimeout(timeoutId);
};

const EffectsAutoPlayer = () => {
  const gameType = useSelector(selectGameType);
  const phase = useSelector(selectPhase);
  const prevPhase = usePrev(phase);
  const isCurrentUserTurn = useIsCurrentUserTurn();
  const prevIsCurrentUserTurn = usePrev(isCurrentUserTurn);
  const gameResults = useSelector(selectGameResults);
  const round = useSelector(selectRound);
  // Число раундов у партии свое (см. checkIfFinalRound)
  const finalRound = useSelector(selectFinalRound);
  const warPhaseStage = useSelector(selectWarPhaseStage);

  const currentPlayer = useSelector(selectCurrentPlayer);
  // Одновременная фаза снабжения сетевой игры: текущий игрок в ней заморожен
  // (якорь слепка хода), а ходит каждый участник за своего действующего
  // игрока — пока тот не закончил свою часть фазы. Вне этой фазы селектор
  // отдает текущего игрока
  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);
  const prevIsParallelSupplyPhase = usePrev(isParallelSupplyPhase);
  const supplyActingPlayer = useSelector(selectSupplyActingPlayer);
  const isGameStarted = useSelector(selectIsGameStarted);
  // Гость только смотрит чужой матч — это не старт и не завершение своей партии
  const isGuest = useSelector((state) => Boolean(state.private.auth?.isGuest));
  const prevIsGameStarted = usePrev(isGameStarted);
  const prevGameResults = usePrev(gameResults);

  // Ключ конкретного этапа найма: раунд + чей это ход. Не флаг «идет ли
  // этап» — при передаче хода в фазе войны этап сбрасывается на найм тем же
  // обновлением стора, что меняет игрока (см. sharedGameStateReducer), а если
  // предыдущий игрок этап найма пропустил, то warPhaseStage вообще не менялся
  // — по одному флагу второй игрок выглядел бы продолжением первого
  const ownRecruitmentStageKey = isCurrentUserTurn
  && phase === PHASES.WAR
  && warPhaseStage === WAR_PHASE_STAGES.RECRUITMENT
  && currentPlayer
    ? `${round}:${currentPlayer.id}`
    : null;
  const preFinalRoundToastShownForKeyRef = useRef(null);

  // То же самое для крысолюдов: их найм за снабжение идет не в фазе войны, а
  // в фазе снабжения — предупреждать надо там же. Фракцию берем у игрока,
  // чье снабжение ведет клиент: в одновременной фазе это свой действующий
  // игрок, а не замороженный текущий
  const ownRatmenSupplyPhaseKey = isCurrentUserTurn
  && phase === PHASES.SUPPLY
  && supplyActingPlayer
  && isPlayerFaction(supplyActingPlayer, FACTIONS.RATMEN)
    ? `${round}:${supplyActingPlayer.id}`
    : null;
  const preFinalRoundRatmenToastShownForKeyRef = useRef(null);

  // Ключ начала своего хода: раунд, фаза, этап и игрок, за которого ходит
  // пользователь. Нужен только на границах одновременной фазы снабжения:
  // флаг «мой ход» там может не падать — игрок, ходивший последним в фазе
  // войны, сразу получает свой ход в одновременном снабжении, а не успевший
  // закончить раздачу до закрытия фазы по таймеру может оказаться первым в
  // строительстве (текущий игрок при этом не меняется)
  const turnPlayer = isParallelSupplyPhase ? supplyActingPlayer : currentPlayer;
  const ownTurnKey = isCurrentUserTurn && turnPlayer
    ? `${round}|${phase}|${warPhaseStage}|${turnPlayer.id}`
    : null;
  const prevOwnTurnKey = usePrev(ownTurnKey);

  useEffect(() => {
    if (gameType === GAME_TYPES.LOCAL || gameResults) {
      return;
    }

    const isOwnTurnStarted = isCurrentUserTurn && !prevIsCurrentUserTurn;
    // Вход в одновременную фазу снабжения или выход из нее со сменой ключа
    // своего хода. Внутри остальных фаз (например, смена этапа войны у того
    // же игрока) ключ не учитывается — там все по-прежнему
    const isParallelSupplyOwnTurnStarted = Boolean(ownTurnKey)
      && ownTurnKey !== prevOwnTurnKey
      && Boolean(isParallelSupplyPhase || prevIsParallelSupplyPhase);

    if (isOwnTurnStarted || isParallelSupplyOwnTurnStarted) {
      setTimeout(() => {
        playPhaseEffectAfterCityCapture(store, phase)
      }, 100);
    }
  }, [
    gameType, phase, isCurrentUserTurn, prevIsCurrentUserTurn, gameResults,
    ownTurnKey, prevOwnTurnKey, isParallelSupplyPhase, prevIsParallelSupplyPhase
  ]);

  // Предупреждение в предпоследнем раунде: дальше найма не будет.
  // Показывается с задержкой и с ПОВТОРНОЙ проверкой состояния — тем же
  // приемом, что и оверлей «Ваш ход» выше: за секунду успевает прийти
  // автопропуск этапа (тогда предупреждать не о чем), а сам оверлей начала
  // хода к этому моменту уже не перекрывается тостом
  useEffect(() => {
    if (gameResults || !ownRecruitmentStageKey || !checkIfPreFinalRoundOf(round, finalRound)) {
      return;
    }
    // Ref, а не usePrev в зависимостях: с prev-флагом в deps эффект
    // перезапускался на первом же перерисовывании внутри секундной задержки,
    // и очистка снимала таймер до показа
    if (preFinalRoundToastShownForKeyRef.current === ownRecruitmentStageKey) {
      return;
    }
    preFinalRoundToastShownForKeyRef.current = ownRecruitmentStageKey;

    return schedulePreFinalRoundToast(
      (currentState) => selectPhase(currentState) === PHASES.WAR
        && selectWarPhaseStage(currentState) === WAR_PHASE_STAGES.RECRUITMENT,
      'Последний шанс нанять новые отряды!\n'
      + `В ${finalRound} раунде найм новых отрядов запрещен!`
    );
  }, [gameResults, round, finalRound, ownRecruitmentStageKey]);

  // То же предупреждение крысолюдам — в фазе снабжения, где идет их найм
  // за снабжение (см. RatmenRecruitmentToolbar)
  useEffect(() => {
    if (gameResults || !ownRatmenSupplyPhaseKey || !checkIfPreFinalRoundOf(round, finalRound)) {
      return;
    }
    if (preFinalRoundRatmenToastShownForKeyRef.current === ownRatmenSupplyPhaseKey) {
      return;
    }
    preFinalRoundRatmenToastShownForKeyRef.current = ownRatmenSupplyPhaseKey;

    return schedulePreFinalRoundToast(
      (currentState) => {
        if (selectPhase(currentState) !== PHASES.SUPPLY) {
          return false;
        }
        const supplyPlayer = selectSupplyActingPlayer(currentState);
        return Boolean(supplyPlayer) && isPlayerFaction(supplyPlayer, FACTIONS.RATMEN);
      },
      'Последний шанс нанять новые отряды за Снабжение!\n'
      + `В ${finalRound} раунде найм новых отрядов запрещен!`
    );
  }, [gameResults, round, finalRound, ownRatmenSupplyPhaseKey]);

  useEffect(() => {
    if (prevPhase === PHASES.SETUP && phase === PHASES.SUPPLY) {
      REGISTRY_SINGLETON.getSoundManager().playGameStart();
    }
  }, [phase, prevPhase]);

  // Рекламные цели по жизненному циклу партии: старт и завершение. Тип партии
  // уходит параметром — в кабинете видно, доходит ли трафик до сетевых матчей
  // или ограничивается игрой с ботами (см. analytics/adTracking.js)
  useEffect(() => {
    if (isGameStarted && !prevIsGameStarted && !isGuest) {
      trackAdGoal(AD_GOALS.MATCH_STARTED, {params: {gameType}});
    }
  }, [isGameStarted, prevIsGameStarted, gameType, isGuest]);

  useEffect(() => {
    const hasResults = Boolean(gameResults && gameResults.length > 0);
    const hadResults = Boolean(prevGameResults && prevGameResults.length > 0);
    if (hasResults && !hadResults && !isGuest) {
      trackAdGoal(AD_GOALS.MATCH_FINISHED, {params: {gameType}});
    }
  }, [gameResults, prevGameResults, gameType, isGuest]);

  useEffect(() => {
    const soundManager = REGISTRY_SINGLETON.getSoundManager();
    if (!soundManager) return;

    if (phase === PHASES.SETUP) {
      soundManager.playBirds();
    }

    if (phase !== PHASES.SETUP && prevPhase === PHASES.SETUP) {
      soundManager.stopBirds();
    }
  }, [phase, prevPhase]);

  return null;
};

/**
 * Ход нового раунда начинается вместе с облетом захваченных городов (см.
 * scenes/CityCaptureAnimation): оверлей «Ваш ход» и звук фазы ждут его
 * конца — иначе легли бы поверх анимации захвата. Так же они ждут роста дохода
 * руин (scenes/RuinsIncomeAnimation): он идет после итогов раунда. Ход к этому
 * времени мог уже кончиться — это проверяет playPhaseEffectIfTurnRemains
 */
const checkIfPhaseEffectHeld = (state) => selectIsCityCaptureAnimationPlaying(state)
  || selectIsRuinsIncomeAnimationAwaited(state);

const playPhaseEffectAfterCityCapture = (store, phase) => {
  if (!checkIfPhaseEffectHeld(store.getState())) {
    playPhaseEffectIfTurnRemains(store, phase);
    return;
  }
  const unsubscribe = store.subscribe(() => {
    if (checkIfPhaseEffectHeld(store.getState())) {
      return;
    }
    unsubscribe();
    playPhaseEffectIfTurnRemains(store, phase);
  });
};

const playPhaseEffectIfTurnRemains = (store, phase) => {
  const currentState = store.getState();
  const currentPhase = selectPhase(currentState);
  // В одновременной фазе снабжения ход — у своего действующего игрока (пока
  // он не закончил свою часть фазы): и автопропуск предсказываем за него, и
  // оверлей показываем с его фракцией и цветом, а не с замороженным текущим
  const isParallelSupplyPhase = selectIsParallelSupplyPhase(currentState);
  const turnPlayer = isParallelSupplyPhase
    ? selectSupplyActingPlayer(currentState)
    : selectCurrentPlayer(currentState);

  // Запускаем звук и показываем оверлей с задержкой.
  // Чтобы избежать запуска в случае, когда фаза автоматически пропускается.
  if (phase === currentPhase && checkIfCurrentUserTurnInState(currentState)) {
    // Задержки выше не всегда хватает, чтобы дождаться события серверного
    // автопропуска — дополнительно предсказываем его той же логикой решений,
    // что у исполнителя автодействий: для пропускаемого хода не играем ни
    // звук фазы, ни оверлей «Ваш ход»
    if (checkIfTurnWillBeAutoCompleted(store, isParallelSupplyPhase ? turnPlayer?.id : undefined)) {
      return;
    }

    playPhaseSound(phase);

    if (turnPlayer && ROUND_PHASES.includes(phase)) {
      showTurnOverlay(
        phase,
        resolveFactionSkinIconPath(turnPlayer.factionId, turnPlayer.selectedSkin),
        turnPlayer.color,
        turnPlayer.selectedSkin,
        () => clearInspectionAfterTurnOverlay(store, phase)
      );
    }
  }
}

// Плашка «Ваш ход» не перехватывает клики: игрок, разглядывавший поле в чужой
// ход, успевает под ней открыть осмотр ячейки уже в своем. Футер снимает осмотр
// только в момент начала хода, поэтому такой клик подчищается, когда плашка ушла
const clearInspectionAfterTurnOverlay = (store, phase) => {
  const state = store.getState();
  if (selectInspectedHexKey(state) && selectPhase(state) === phase && checkIfCurrentUserTurnInState(state)) {
    store.dispatch(clearInspectedHex());
  }
};

const playPhaseSound = (phase) => {
  switch (phase) {
    case PHASES.SUPPLY:
      REGISTRY_SINGLETON.getSoundManager().playPhaseSupply();
      break;
    case PHASES.CONSTRUCTION:
      REGISTRY_SINGLETON.getSoundManager().playPhaseConstruction();
      break;
    case PHASES.WAR:
      REGISTRY_SINGLETON.getSoundManager().playPhaseWar();
      break;
    default:
  }
}

export default EffectsAutoPlayer;
